import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { printSpecializedVouchersReport } from '../utils/printReports';

interface Voucher {
  _id: string;
  voucherNumber: string;
  voucherDate: string;
  voucherType: 'receipt' | 'payment' | 'journal';
  amount: number;
  operationNumber?: string;
  notes?: string;
  isApproved: boolean;
  templateId?: {
    _id: string;
    name: string;
    voucherType: string;
  };
  debitAccountId?: {
    _id: string;
    name: string;
    accountNumber: string;
  };
  creditAccountId?: {
    _id: string;
    name: string;
    accountNumber: string;
  };
  branch?: {
    _id: string;
    name: string;
  };
  createdBy?: {
    _id: string;
    username: string;
  };
  appliedFeeGroupId?: {
    _id: string;
    name: string;
  };
}

interface Summary {
  totalAmount: number;
  totalCount: number;
  receiptCount: number;
  paymentCount: number;
  journalCount: number;
  receiptSum: number;
  paymentSum: number;
  journalSum: number;
}

interface Branch {
  _id: string;
  name: string;
}

interface Template {
  _id: string;
  name: string;
  voucherType: string;
}

const SpecializedVouchersReportPage: React.FC = () => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    branchId: 'all',
    templateIds: [] as string[],
    status: 'all'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // جلب البيانات عند تحميل الصفحة
  useEffect(() => {
    fetchBranches();
    fetchTemplates();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/api/branches');
      console.log('الفروع المحملة:', response.data);
      // API يرجع كائن يحتوي على branches
      const branchesData = response.data.branches || [];
      setBranches(branchesData);
    } catch (error) {
      console.error('خطأ في جلب الفروع:', error);
      setBranches([]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/finance/specialized-vouchers/templates');
      // التأكد من أن البيانات مصفوفة
      const templatesData = Array.isArray(response.data) ? response.data : [];
      console.log('القوالب المحملة:', templatesData);
      setTemplates(templatesData);
    } catch (error) {
      console.error('خطأ في جلب القوالب:', error);
      setTemplates([]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/reports/vouchers/specialized-vouchers', {
        params: {
          ...filters,
          page: page + 1,
          limit: rowsPerPage
        }
      });

      setVouchers(response.data.vouchers);
      setSummary(response.data.summary);
      setTotalCount(response.data.pagination.totalItems);
    } catch (error: any) {
      console.error('خطأ في جلب البيانات:', error);
      setError(error.response?.data?.error || 'حدث خطأ أثناء جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchData();
  };

  const handleExport = async () => {
    try {
      const response = await axios.get('/api/reports/vouchers/specialized-vouchers/export', {
        params: filters,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `specialized-vouchers-report-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('خطأ في تصدير التقرير:', error);
      setError('حدث خطأ أثناء تصدير التقرير');
    }
  };

  const handlePrint = () => {
    printSpecializedVouchersReport(vouchers, summary, filters, branches, templates);
  };

  const getVoucherTypeLabel = (type: string) => {
    switch (type) {
      case 'receipt':
        return 'سند قبض';
      case 'payment':
        return 'سند صرف';
      case 'journal':
        return 'سند قيد';
      default:
        return type;
    }
  };

  const getVoucherTypeColor = (type: string) => {
    switch (type) {
      case 'receipt':
        return 'success';
      case 'payment':
        return 'error';
      case 'journal':
        return 'info';
      default:
        return 'default';
    }
  };

  const getVoucherTypeIcon = (type: string) => {
    switch (type) {
      case 'receipt':
        return <ReceiptIcon />;
      case 'payment':
        return <PaymentIcon />;
      case 'journal':
        return <DescriptionIcon />;
      default:
        return undefined;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: ar });
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            تقرير السندات المتخصصة
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="تحديث">
              <IconButton onClick={fetchData} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={loading}
            >
              تصدير Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={loading}
            >
              طباعة
            </Button>
          </Box>
        </Box>

        {/* الفلاتر */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="من تاريخ"
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="إلى تاريخ"
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="الفرع"
              value={filters.branchId}
              onChange={(e) => handleFilterChange('branchId', e.target.value)}
            >
              <MenuItem value="all">الكل</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch._id} value={branch._id}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>صفحات السندات / القوالب</InputLabel>
              <Select
                multiple
                value={filters.templateIds}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilters(prev => ({
                    ...prev,
                    templateIds: typeof value === 'string' ? value.split(',') : value
                  }));
                }}
                input={<OutlinedInput label="صفحات السندات / القوالب" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return 'الكل';
                  }
                  return selected
                    .map(id => templates.find(t => t._id === id)?.name || id)
                    .join(', ');
                }}
              >
                {templates.map((template) => (
                  <MenuItem key={template._id} value={template._id}>
                    <Checkbox checked={filters.templateIds.indexOf(template._id) > -1} />
                    <ListItemText primary={template.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="حالة السند"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="all">الكل</MenuItem>
              <MenuItem value="approved">مؤكد</MenuItem>
              <MenuItem value="pending">قيد الانتظار</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={loading}
              fullWidth
            >
              تطبيق الفلاتر
            </Button>
          </Grid>
        </Grid>

        {/* الإحصائيات */}
        {summary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ReceiptIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      سندات القبض
                    </Typography>
                  </Box>
                  <Typography variant="h6">{summary.receiptCount}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(summary.receiptSum)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PaymentIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      سندات الصرف
                    </Typography>
                  </Box>
                  <Typography variant="h6">{summary.paymentCount}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(summary.paymentSum)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DescriptionIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      سندات القيد
                    </Typography>
                  </Box>
                  <Typography variant="h6">{summary.journalCount}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(summary.journalSum)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    إجمالي السندات
                  </Typography>
                  <Typography variant="h6">{summary.totalCount}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    المجموع الكلي
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(summary.totalAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* الجدول */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>رقم السند</TableCell>
                    <TableCell>اسم القالب</TableCell>
                    <TableCell>نوع السند</TableCell>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>الفرع</TableCell>
                    <TableCell>الحساب المدين</TableCell>
                    <TableCell>الحساب الدائن</TableCell>
                    <TableCell align="right">المبلغ</TableCell>
                    <TableCell>رقم العملية</TableCell>
                    <TableCell>الحالة</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vouchers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        لا توجد بيانات
                      </TableCell>
                    </TableRow>
                  ) : (
                    vouchers.map((voucher) => (
                      <TableRow key={voucher._id}>
                        <TableCell>{voucher.voucherNumber}</TableCell>
                        <TableCell>{voucher.templateId?.name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={getVoucherTypeLabel(voucher.voucherType)}
                            size="small"
                            color={getVoucherTypeColor(voucher.voucherType)}
                            icon={getVoucherTypeIcon(voucher.voucherType)}
                          />
                        </TableCell>
                        <TableCell>{formatDate(voucher.voucherDate)}</TableCell>
                        <TableCell>{voucher.branch?.name || '-'}</TableCell>
                        <TableCell>{voucher.debitAccountId?.name || '-'}</TableCell>
                        <TableCell>{voucher.creditAccountId?.name || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(voucher.amount)}</TableCell>
                        <TableCell>{voucher.operationNumber || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={voucher.isApproved ? 'مؤكد' : 'قيد الانتظار'}
                            size="small"
                            color={voucher.isApproved ? 'success' : 'warning'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="عدد الصفوف:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`
              }
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SpecializedVouchersReportPage;
